import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu, Divider } from '@arco-design/web-react';
import { ToolItem } from '../ToolItem';
import { useTiptapEditor } from '../Tools/TiptapEditorContext';
import {
	getShadowRoot,
	useBlock,
	useEditorProps,
	useFocusIdx,
	beginFormatInteraction,
	endFormatInteraction,
} from '@bay-email/editor';
import { getContextMergeTags } from '@extensions/utils/getContextMergeTags';
import { getMergeTagsForContext } from '@extensions/utils/getMergeTagsForContext';
import { matchesMergeTagQuery } from '@extensions/utils/matchesMergeTagQuery';
import {
	t,
	dedupeMergeTagKeys,
	getMergeTagGroup,
	getMergeTagGroupLabel,
	getMergeTagGroupSortWeight,
	getMergeTagLabel,
	hasMergeTagKey,
	getAncestorCustomListingIdx,
} from '@bay-email/core';
import BaySearch from '@bay-ui/BaySearch';

function formatFieldLabel(fieldKey: string): string {
	return fieldKey
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim()
		.replace(/\s+/g, ' ')
		.replace(/\b\w/g, char => char.toUpperCase());
}

export interface MergeTagsProps {
	execCommand: (cmd: string, value: any) => void;
	onBeforeInteract?: () => void;
}

interface MergeTagInsertPayload {
	html: string;
	range: Range | null;
	requiresExactRange: boolean;
}

export function MergeTags(props: MergeTagsProps) {
	const { execCommand, onBeforeInteract } = props;
	const [searchQuery, setSearchQuery] = useState('');
	const selectedTextRef = useRef('');
	const selectedRangeRef = useRef<Range | null>(null);
	const formatGuardHeldRef = useRef(false);
	const { focusIdx } = useFocusIdx();
	const tiptapEditor = useTiptapEditor();

	// Release the guard if this unmounts while the dropdown is open.
	useEffect(
		() => () => {
			if (formatGuardHeldRef.current) {
				endFormatInteraction();
				formatGuardHeldRef.current = false;
			}
		},
		[],
	);
	const { mergeTags = {}, mergeTagGenerate } = useEditorProps();
	const { values, focusBlock } = useBlock();

	// Capture the live range at trigger-press — INCLUDING a collapsed caret. This is the
	// exact insertion point; we freeze it here so it can't drift. (Previously collapsed
	// carets returned null, forcing a fallback to the mutable lastCaretRangeRef, which
	// selectionchange events overwrote while the dropdown was open — causing the tag to
	// land at the start of the block or mid-word, inconsistently.)
	const captureSelectedRange = useCallback((): Range | null => {
		const shadowSelection = getShadowRoot()?.getSelection?.();
		if (shadowSelection && shadowSelection.rangeCount > 0) {
			return shadowSelection.getRangeAt(0).cloneRange();
		}

		const windowSelection = typeof window !== 'undefined' ? window.getSelection() : null;
		if (windowSelection && windowSelection.rangeCount > 0) {
			return windowSelection.getRangeAt(0).cloneRange();
		}

		return null;
	}, []);

	const captureSelectedText = useCallback((): string => {
		const shadowSelection = getShadowRoot()?.getSelection?.();
		const windowSelection = typeof window !== 'undefined' ? window.getSelection() : null;

		return String(
			shadowSelection?.toString() || windowSelection?.toString() || '',
		)
			.replace(/\s+/g, ' ')
			.trim();
	}, []);

	// Listing fields only exist for blocks that actually have somewhere to get a
	// listing FROM: inside a Custom Listing Wrapper, or inside a Listing
	// section's column. Anywhere else they are removed from the dropdown
	// entirely — not disabled — so the only way to design with listing data is
	// to use one of those two containers.
	const hasListingScope = useMemo(
		() => Boolean(getAncestorCustomListingIdx((values as any)?.content, focusIdx)),
		[values, focusIdx],
	);

	const withoutListingTags = useCallback(
		(tags: Record<string, any>) =>
			Object.fromEntries(
				Object.entries(tags || {}).filter(
					([key]) => key !== 'listing' && getMergeTagGroup(key) !== 'listing',
				),
			),
		[],
	);

	const contextMergeTags = useMemo(() => {
		const tags = getContextMergeTags(mergeTags, values, focusIdx);
		const forContext = getMergeTagsForContext(tags, 'no-image-src');
		return hasListingScope ? forContext : withoutListingTags(forContext);
	}, [mergeTags, values, focusIdx, hasListingScope, withoutListingTags]);

	const menuData = useMemo(() => {
		const normalizedContextMergeTags: Record<string, Record<string, any>> = {};

		Object.entries(contextMergeTags || {}).forEach(([groupKey, groupData]) => {
			if (groupData && typeof groupData === 'object') {
				normalizedContextMergeTags[groupKey] = groupData as Record<string, any>;
				return;
			}

			if (typeof groupData === 'string' || typeof groupData === 'number') {
				const resolvedGroupKey = getMergeTagGroup(groupKey);
				if (!normalizedContextMergeTags[resolvedGroupKey]) {
					normalizedContextMergeTags[resolvedGroupKey] = {};
				}
				normalizedContextMergeTags[resolvedGroupKey][groupKey] = groupData;
			}
		});

		const resolveMergeTagKey = (groupKey: string, fieldKey: string): string => {
			const dottedKey = `${groupKey}.${fieldKey}`;
			if (hasMergeTagKey(dottedKey)) return dottedKey;
			if (hasMergeTagKey(fieldKey)) return fieldKey;
			return dottedKey;
		};

		const getFieldLabel = (groupKey: string, fieldKey: string): string => {
			const fullKey = resolveMergeTagKey(groupKey, fieldKey);
			return getMergeTagLabel(fullKey) ?? formatFieldLabel(fieldKey);
		};

		return {
			normalizedContextMergeTags,
			resolveMergeTagKey,
			getFieldLabel,
		};
	}, [contextMergeTags]);

	const onMenuClick = useCallback(
		(key: string) => {
			if (!key || !hasMergeTagKey(key)) return;

			// The editor owns its selection and caret, so none of the range plumbing
			// below applies: a selected word becomes the tag's fallback and is replaced.
			if (tiptapEditor) {
				const { from, to } = tiptapEditor.state.selection;
				const fallback = tiptapEditor.state.doc
					.textBetween(from, to, ' ')
					.replace(/\s+/g, ' ')
					.replace(/[{}]/g, '')
					.trim();
				execCommand('insertMergeTag', {
					html: fallback
						? `{{#if ${key}}}${mergeTagGenerate(key)}{{else}}${fallback}{{/if}}`
						: mergeTagGenerate(key),
				});
				return;
			}

			const selectedRange = selectedRangeRef.current;
			const selectedText = selectedRange
				? selectedTextRef.current || captureSelectedText()
				: '';

			const sanitizedFallback = selectedText
				.replace(/[{}]/g, '')
				.trim();

			const mergeTag = sanitizedFallback
				? `{{#if ${key}}}${mergeTagGenerate(key)}{{else}}${sanitizedFallback}{{/if}}`
				: mergeTagGenerate(key);

			const payload: MergeTagInsertPayload = {
				html: mergeTag,
				range: selectedRange ? selectedRange.cloneRange() : null,
				requiresExactRange: Boolean(sanitizedFallback),
			};
			execCommand('insertMergeTag', payload);
			selectedTextRef.current = '';
			selectedRangeRef.current = null;
		},
		[captureSelectedText, mergeTagGenerate, execCommand, tiptapEditor],
	);

	// Create the grouped menu
	const orderedGroupKeys = Object.keys(menuData.normalizedContextMergeTags).sort(
		(a, b) => {
			const weightDiff =
				getMergeTagGroupSortWeight(a) - getMergeTagGroupSortWeight(b);
			if (weightDiff !== 0) return weightDiff;
			return a.localeCompare(b);
		},
	);

	const normalizedSearch = searchQuery.trim().toLowerCase();

	const dropList = (
		<Menu
			onClickMenuItem={onMenuClick}
			style={{
				maxHeight: 'unset',
				minWidth: 300,
			}}
			className='p-0 border-0 shadow'
		>
			<div
				className='p-4 rounded-top-1 bg-white'
				onMouseDown={e => e.stopPropagation()}
			>
				<BaySearch
					hideMenu
					searchIcon
					placeholder={t('Search')}
					searchInputValue={searchQuery}
					handleSearchInput={(val: string) => setSearchQuery(val ?? '')}
				/>
			</div>
			<Divider style={{ margin: '0' }} />
			<div
				className='overflow-auto pt-3 pb-2'
				style={{ maxHeight: 431 }}
			>
				{orderedGroupKeys.map((groupKey, groupIndex) => {
					const groupData = menuData.normalizedContextMergeTags[groupKey];
					const groupDisplayName = getMergeTagGroupLabel(groupKey);
					if (!groupData || typeof groupData !== 'object') return null;

					const dedupedFieldKeys = dedupeMergeTagKeys(
						Object.keys(groupData).map(fieldKey =>
							menuData.resolveMergeTagKey(groupKey, fieldKey),
						),
					).map(fullKey =>
						fullKey.includes('.')
							? fullKey.split('.').slice(1).join('.')
							: fullKey,
					);

					const menuItems = dedupedFieldKeys
						.filter(fieldKey => {
							const fieldValue = groupData[fieldKey];
							const fullKey = menuData.resolveMergeTagKey(groupKey, fieldKey);
							const isValidValue =
								typeof fieldValue === 'string' || typeof fieldValue === 'number';
							if (!isValidValue || !hasMergeTagKey(fullKey)) return false;
							if (!normalizedSearch) return true;
							const label = menuData.getFieldLabel(groupKey, fieldKey) ?? fieldKey;
							return matchesMergeTagQuery(
								label,
								fieldKey,
								fullKey,
								normalizedSearch,
							);
						})
						.sort((a, b) =>
							menuData
								.getFieldLabel(groupKey, a)
								.localeCompare(menuData.getFieldLabel(groupKey, b), undefined, {
									sensitivity: 'base',
									numeric: true,
								}),
						)
						.map(fieldKey => {
							const fieldDisplayName = menuData.getFieldLabel(groupKey, fieldKey);
							const mergeTagKey = menuData.resolveMergeTagKey(
								groupKey,
								fieldKey,
							);
							return (
								<Menu.Item key={mergeTagKey}>
									{fieldDisplayName}
								</Menu.Item>
							);
						});

					if (menuItems.length === 0) return null;

					return (
						<div
							key={groupKey}
							className='px-2 mx-1'
						>
							{groupIndex > 0 && <Divider className='my-3' />}
							<Menu.ItemGroup
								title={
									<div className='text-500 fw-bold text-uppercase mb-1'>
										{groupDisplayName}
									</div>
								}
							>
								{menuItems}
							</Menu.ItemGroup>
						</div>
					);
				})}
			</div>
		</Menu>
	);

	return (
		<div
			onMouseDown={e => {
				onBeforeInteract?.();
				selectedRangeRef.current = captureSelectedRange();
				selectedTextRef.current = captureSelectedText();
				e.preventDefault();
			}}
		>
			<Dropdown
				droplist={dropList}
				trigger='click'
				position='bl'
				onVisibleChange={visible => {
					if (visible) {
						// Hold the format guard while the dropdown is open so the canvas stays
						// frozen and the captured caret range can't be invalidated by a rebuild
						// before we insert (which made the tag land at the start of the block).
						if (!formatGuardHeldRef.current) {
							beginFormatInteraction();
							formatGuardHeldRef.current = true;
						}
						if (!selectedRangeRef.current) {
							selectedRangeRef.current = captureSelectedRange();
						}
						if (!selectedTextRef.current) {
							selectedTextRef.current = captureSelectedText();
						}
						return;
					}
					if (formatGuardHeldRef.current) {
						endFormatInteraction();
						formatGuardHeldRef.current = false;
					}
					setSearchQuery('');
				}}
				triggerProps={{
					autoFitPosition: true,
					updateOnScroll: true,
				}}
			>
				<ToolItem
					icon={(
						<span style={{ fontSize: 12 }}>
							{'{'}
							{t('Dynamic Fields')}
							{'}'}
						</span>
					)}
					style={{ width: 'auto', padding: '0 8px' }}
				/>
			</Dropdown>
		</div>
	);
}
